/**
 * Toolbar Rail Drag-to-Dock
 * Photoshop-style rail move: dragging the toolbar's empty body area (not an
 * icon, not a resize seam) starts a dock drag; the shared dockSnapResolver
 * turns the pointer position into an edge or a relative insert (above/below
 * or left/right of another docked entry), and dropping calls onDock with the
 * final { side, index } decision — the same interaction model and overlay
 * visuals the sidepanels use.
 */

import { useRef } from 'react';
import { DockSide, DockTarget } from '../store/useDockLayoutStore';
import { useDockLayoutStore } from '../store/useDockLayoutStore';
import {
    resolveDockTarget,
    setLiveDockEntryRects,
    clearLiveDockEntryRects,
} from '../dock/dockSnapResolver';
import { collectDockEntryRects } from '../dock/dockStripRects';
import { dockOverlayNode } from '../dock/dockOverlay';
import { createRoot, Root } from 'react-dom/client';

interface ToolbarRailDragArgs {
    /** Root element of the toolbar rail */
    railRef: React.RefObject<HTMLElement | null>;
    /** Layout lock also freezes rail dragging */
    locked: boolean;
    /** Called with the resolved target on drop (never called on cancel) */
    onDock: (target: DockTarget) => void;
}

interface ToolbarRailDragResult {
    /** Attach to the rail root: starts a rail drag on empty-area mousedown */
    onMouseDown: (e: React.MouseEvent) => void;
}

export function useToolbarRailDrag({
    locked,
    onDock,
}: ToolbarRailDragArgs): ToolbarRailDragResult {
    const overlayHostRef = useRef<HTMLDivElement | null>(null);
    const overlayRootRef = useRef<Root | null>(null);

    /** Strip side per entry id from the shared dock layout store */
    const getEntrySide = (entryId: string): DockSide | undefined => {
        const { strips } = useDockLayoutStore.getState();
        return (Object.keys(strips) as DockSide[]).find((s) =>
            strips[s].includes(entryId),
        );
    };

    /** Paint the current overlay (edge band or entry-relative insert band) */
    const paintOverlay = (
        target: ReturnType<typeof resolveDockTarget>,
    ) => {
        const root = overlayRootRef.current;
        if (!root) return;
        root.render(dockOverlayNode({ side: target.side, overlayRect: target.overlayRect }));
    };

    const onMouseDown = (e: React.MouseEvent) => {
        if (locked || e.button !== 0) return;
        // Empty body area only: icons/accessories keep their own gestures
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        document.body.classList.add('toolbar-rail-dragging');

        const overlayHost = document.createElement('div');
        overlayHostRef.current = overlayHost;
        document.body.appendChild(overlayHost);
        overlayRootRef.current = createRoot(overlayHost);

        const cleanup = () => {
            document.body.classList.remove('toolbar-rail-dragging');
            overlayRootRef.current?.unmount();
            overlayRootRef.current = null;
            overlayHost.remove();
            overlayHostRef.current = null;
            clearLiveDockEntryRects();
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };

        const handleMove = (ev: MouseEvent) => {
            // Refresh the snapshot each move: cheap enough at drag frequency
            // and stays correct while other entries animate
            setLiveDockEntryRects(
                collectDockEntryRects().rects,
                getEntrySide,
            );
            paintOverlay(resolveDockTarget(ev.clientX, ev.clientY));
        };

        const handleUp = (ev: MouseEvent) => {
            setLiveDockEntryRects(
                collectDockEntryRects().rects,
                getEntrySide,
            );
            const target = resolveDockTarget(ev.clientX, ev.clientY);
            cleanup();
            if (target.side) onDock({ side: target.side, index: target.index });
        };

        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
    };

    return { onMouseDown };
}
